import Dexie, { type EntityTable } from 'dexie'
import type {
  Cycle,
  Recovery,
  Sleep,
  Workout,
  UserBasicProfile,
  UserBodyMeasurement,
} from '@/api/types'

/**
 * Local cache of the user's Whoop data. The point: every chart and pivot in
 * the app reads from here, never from the Whoop API directly. That makes
 * compound queries the iOS app can't do — day-of-week pivots, multi-year
 * heatmaps, cross-metric correlations — instant and offline.
 */

// We add a `day` field (YYYY-MM-DD in cycle local timezone) to every dated
// record so we can index and pivot by day cheaply.
export interface CycleRow extends Cycle {
  day: string
}
export interface RecoveryRow extends Recovery {
  day: string
}
export interface SleepRow extends Sleep {
  day: string // calendar day the sleep ENDED on
}
export interface WorkoutRow extends Workout {
  day: string
}

export interface SyncCursor {
  resource: 'cycle' | 'recovery' | 'sleep' | 'workout'
  // Highest `start` we've seen for this resource. Subsequent syncs query
  // start=this-3 days, end=now to catch updates and new records.
  high_water_iso: string | null
  last_run_iso: string
}

export interface ProfileRow extends UserBasicProfile {
  id: 'me'
  fetched_at: string
}
export interface BodyRow extends UserBodyMeasurement {
  id: 'me'
  fetched_at: string
}

export class LensifyDB extends Dexie {
  cycles!: EntityTable<CycleRow, 'id'>
  recoveries!: EntityTable<RecoveryRow, 'cycle_id'>
  sleep!: EntityTable<SleepRow, 'id'>
  workouts!: EntityTable<WorkoutRow, 'id'>
  cursors!: EntityTable<SyncCursor, 'resource'>
  profile!: EntityTable<ProfileRow, 'id'>
  body!: EntityTable<BodyRow, 'id'>

  constructor() {
    super('whooplens')
    this.version(1).stores({
      // Primary key first, then indexed fields.
      cycles: 'id, day, start, score_state',
      recoveries: 'cycle_id, day, score_state',
      sleep: 'id, day, start, end, nap, score_state',
      workouts: 'id, day, start, sport_id, score_state',
      cursors: 'resource',
      profile: 'id',
      body: 'id',
    })
  }
}

export const db = new LensifyDB()

/**
 * Pull a YYYY-MM-DD label out of a Whoop timestamp + timezone offset.
 * Whoop returns `start` as UTC and `timezone_offset` as e.g. `-05:00`.
 */
export function dayInLocalTz(iso: string, tzOffset: string): string {
  const d = new Date(iso)
  // Apply the offset manually so the result is "calendar day in the user's TZ".
  const [sign, hh, mm] = parseOffset(tzOffset)
  const ms = (Number(hh) * 60 + Number(mm)) * 60_000
  const local = new Date(d.getTime() + (sign === '-' ? -ms : ms))
  return local.toISOString().slice(0, 10)
}

function parseOffset(o: string): ['+' | '-', string, string] {
  if (o === 'Z' || o === 'z') return ['+', '00', '00']
  const sign = o[0] === '-' ? '-' : '+'
  const [hh, mm] = o.slice(1).split(':')
  return [sign, hh ?? '00', mm ?? '00']
}
